import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["disabled", "options", "placeholder", "onChange"];
import { Controllable } from '../Controllable';
import { filterProps } from '../../../utils/filterProps';
import { useMapTokenPropsToCSS } from '../../../hooks/useMapTokenPropsToCSS';
import styled from 'foundations-theming/utils';
import { FieldsetContextConsumer } from '../../context/FieldsetContext';
import { VENUS_LAYER } from '../../constants';
import { getFocusStyleObject } from '../../utils/focus/focus.style';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const PLACEHOLDER = '__PLACEHOLDER__';
const renderOption = ({
  disabled,
  text = '',
  value = '',
  options: nestedOptions
}) => nestedOptions ? /*#__PURE__*/_jsx("optgroup", {
  disabled: disabled,
  label: text || value,
  children: nestedOptions.map(renderOption)
}, text + value) : /*#__PURE__*/_jsx("option", {
  disabled: disabled,
  value: value,
  children: text || value
}, text + value);
const StyledOuterWrapper = styled.div.withConfig({
  displayName: "SelectInput__StyledOuterWrapper",
  componentId: "sc-4yw97n-0"
})(({
  $cssStyles
}) => {
  return Object.assign({}, $cssStyles);
});
const StyledInnerWrapper = styled.div.withConfig({
  displayName: "SelectInput__StyledInnerWrapper",
  componentId: "sc-4yw97n-1"
})(({
  theme,
  $disabled,
  $size = 'default'
}) => {
  const isSmall = $size === 'small';
  const caretSize = `.375em`;
  const {
    fontSize,
    lineHeight
  } = isSmall ? theme.typography['body-125'] : theme.typography['body-200'];
  const height = isSmall ? '32px' : '40px';
  return {
    fontSize,
    height,
    lineHeight: `calc(${lineHeight} - 2px)`,
    border: `${theme.borderWidth['100']} solid ${theme.color['border-core-default']}`,
    borderRadius: theme.borderRadius[100],
    display: 'inline-block',
    position: 'relative',
    verticalAlign: 'middle',
    width: '100%',
    '&::after': Object.assign({
      border: `${caretSize} solid ${theme.color['fill-transparent-default']}`,
      borderTopColor: theme.color['border-info-default'],
      content: '""',
      display: 'block',
      marginTop: `calc(-${caretSize}/2)`,
      position: 'absolute',
      pointerEvents: 'none',
      right: `calc(${theme.space[800]} / 2)`,
      top: '50%',
      verticalAlign: 'middle',
      zIndex: VENUS_LAYER
    }, $disabled ? {
      borderTopColor: theme.color['border-core-default']
    } : {})
  };
});
const StyledSelect = styled.select.withConfig({
  displayName: "SelectInput__StyledSelect",
  componentId: "sc-4yw97n-2"
})(({
  theme,
  $disabled,
  $size = 'default'
}) => {
  const isSmall = $size === 'small';
  const paddingBlockEnd = isSmall ? theme.space[100] : `calc(${theme.space[100]} + ${theme.space[50]})`;
  const paddingBlockStart = isSmall ? theme.space[100] : theme.space[200];
  const disabledStyles = $disabled ? {
    backgroundColor: theme.color['fill-field-disabled'],
    borderColor: theme.color['border-secondary-disabled'],
    color: theme.color['text-secondary-disabled'],
    cursor: 'not-allowed'
  } : {};
  const {
    fontSize
  } = isSmall ? theme.typography['body-125'] : theme.typography['body-200'];
  return Object.assign({
    fontSize,
    paddingBlockEnd,
    paddingBlockStart,
    appearance: 'none',
    backgroundColor: theme.color['fill-field-default'],
    border: `${theme.borderWidth['100']} solid ${theme.color['fill-transparent-default']}`,
    borderRadius: theme.borderRadius[100],
    height: '100%',
    outline: 'none',
    paddingInlineEnd: theme.space[1000],
    paddingInlineStart: theme.space[400],
    width: '100%'
  }, getFocusStyleObject({
    theme,
    increaseOutlineOffset: true
  }), disabledStyles);
});
const SelectInputComponent = _ref => {
  let {
      disabled,
      options,
      placeholder,
      onChange
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const {
    tokenProps
  } = filterProps(rest);
  const cssStyles = useMapTokenPropsToCSS(tokenProps);
  return /*#__PURE__*/_jsx(FieldsetContextConsumer, {
    children: ({
      size
    }) => /*#__PURE__*/_jsx(StyledOuterWrapper, {
      $cssStyles: cssStyles,
      children: /*#__PURE__*/_jsx(StyledInnerWrapper, {
        $disabled: disabled,
        $size: size,
        children: /*#__PURE__*/_jsxs(StyledSelect, Object.assign({
          $disabled: disabled,
          $size: size,
          disabled: disabled,
          onChange: onChange
        }, rest, {
          children: [placeholder && /*#__PURE__*/_jsx("option", {
            disabled: true,
            value: PLACEHOLDER,
            children: placeholder
          }, "placeholder"), options.map(renderOption)]
        }))
      })
    })
  });
};
export const SelectInput = Controllable(SelectInputComponent);
SelectInput.displayName = 'SelectInput';